/**
 * User/Auth API Service
 * Handles user and authentication-related API calls
 */

import { ApiClient } from '../core/api-client';
import { isApiError } from '../core/errors';
import { logger } from '../../utils/logger';

export interface User {
  id: number;
  firebaseUid?: string;
  userType?: string;
  name: string;
  legalName: string;
  email: string | null;
  phone: string | null;
  whatsAppNumber: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  pincode: string | null;
  pan: string | null;
  birthdate: string | null;
  anniversaryDate?: string | null;
  avatar?: string;
  photoUrl?: string;
  role?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AuthResponse {
  data: {
    user: User;
    token?: string;
  };
}

interface UserPayload {
  id: number;
  firebase_uid?: string;
  user_type?: string;
  legalName?: string;
  email?: string | null;
  phone?: string | null;
  whatsAppNumber?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  pincode?: string | null;
  pan?: string | null;
  birthdate?: string | null;
  anniversaryDate?: string | null;
  photo_url?: string;
  role?: string;
  created_at?: string;
  updated_at?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface UserProfileUpdate {
  userType?: string;
  phone: string;
  whatsAppNumber?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  pan?: string;
  email: string;
  birthdate?: string;
  anniversaryDate?: string | null;
  legalName?: string;
  photoUrl?: string;
}

const normalizeUser = (payload: UserPayload): User => ({
  id: payload.id,
  firebaseUid: payload.firebase_uid,
  userType: payload.user_type,
  name: payload.legalName || '',
  legalName: payload.legalName || '',
  email: payload.email ?? null,
  phone: payload.phone ?? null,
  whatsAppNumber: payload.whatsAppNumber ?? null,
  address: payload.address ?? null,
  city: payload.city ?? null,
  state: payload.state ?? null,
  country: payload.country ?? null,
  pincode: payload.pincode ?? null,
  pan: payload.pan ?? null,
  birthdate: payload.birthdate ?? null,
  anniversaryDate: payload.anniversaryDate ?? null,
  avatar: payload.photo_url,
  photoUrl: payload.photo_url,
  role: payload.role,
  createdAt: payload.createdAt || payload.created_at,
  updatedAt: payload.updatedAt || payload.updated_at,
});

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

export class UserService {
  private client: ApiClient;

  constructor() {
    this.client = ApiClient.getInstance(API_BASE_URL);
  }

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await this.client.get<UserPayload>('/user/me', {
        credentials: 'include',
      });

      return response.data ? normalizeUser(response.data) : null;
    } catch (error) {
      if (isApiError(error)) {
        if (error.statusCode === 401) {
          logger.warn('User not authenticated');
          return null;
        }

        if (error.statusCode === 404) {
          logger.warn('User profile not found');
          return null;
        }
      }

      logger.error('Failed to fetch current user', error);
      return null;
    }
  }

  /**
   * Get user by ID
   */
  async getUserById(userId: string): Promise<User | null> {
    try {
      const response = await this.client.get<UserPayload>(`/user/${userId}`, {
        credentials: 'include',
      });

      return response.data ? normalizeUser(response.data) : null;
    } catch (error) {
      if (isApiError(error) && error.statusCode === 404) {
        logger.warn(`User ${userId} not found`);
        return null;
      }

      logger.error('Failed to fetch user', error);
      return null;
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(data: UserProfileUpdate): Promise<User | null> {
    try {
      await this.client.put('/user/me', {
        user_type: data.userType,
        phone: data.phone,
        whatsAppNumber: data.whatsAppNumber,
        address: data.address,
        city: data.city,
        state: data.state,
        country: data.country,
        pincode: data.pincode,
        pan: data.pan,
        email: data.email,
        birthdate: data.birthdate,
        anniversaryDate: data.anniversaryDate ?? undefined,
        legalName: data.legalName,
        photo_url: data.photoUrl,
      }, {
        credentials: 'include',
      });

      return this.getCurrentUser();
    } catch (error) {
      logger.error('Failed to update profile', error);
      throw error;
    }
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    try {
      await this.client.post('/auth/logout', {}, { credentials: 'include' });
    } catch (error) {
      logger.error('Failed to logout', error);
      // Don't throw - logout should always succeed client-side
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      const user = await this.getCurrentUser();
      return !!user;
    } catch {
      return false;
    }
  }
}

// Export singleton instance
export const userService = new UserService();
